import React, { useEffect, useState } from 'react';
import Navigator from './Routes/Navigator';
import { Provider, useAtom, useSetAtom } from 'jotai';
import { DarkModeProvider } from './Components/DarkModeContext';
import AccessGate from './Components/AccessGate';
import NotificationPromptModal from './Components/NotificationPromptModal';
import VideochatHost from './Components/VideochatHost';
import PresenceHost from './Components/PresenceHost';
import Loading from './Components/Loading';
import { authUserAtom, darkModeAtom, userRoleAtom, setDataUserAtom } from './Services/store/atoms';
import { webPathFromDeepLink } from './Utils/deepLink';
import axios from 'axios';
import config from './Config';
import 'animate.css';

function AppContent() {
  const [authUser] = useAtom(authUserAtom);
  const [darkMode] = useAtom(darkModeAtom);
  const [userRole] = useAtom(userRoleAtom);
  const setDataUser = useSetAtom(setDataUserAtom);
  const [oneSignalReady, setOneSignalReady] = useState(false);
  const [hasPushId, setHasPushId] = useState(null);
  // Se recuerda por pestaña/sesión del navegador: un "Más tarde" o un permiso
  // bloqueado no vuelve a molestar al recargar, pero sí al volver a entrar otro día.
  const [dismissedPrompt, setDismissedPromptState] = useState(() => {
    try { return sessionStorage.getItem('notifPromptDismissed') === '1'; } catch (e) { return false; }
  });
  const setDismissedPrompt = (value) => {
    setDismissedPromptState(value);
    try { sessionStorage.setItem('notifPromptDismissed', value ? '1' : '0'); } catch (e) { /* noop */ }
  };

  // El perfil guardado en localStorage es una foto del momento del login: si algo
  // cambió del lado del servidor mientras la sesión seguía abierta (otro dispositivo,
  // un cambio manual, etc.), esa foto queda vieja y las redirecciones de RouteCheck
  // (p.ej. a /client/complete-profile) terminan usando datos desactualizados. Por eso
  // al bootear la app con una sesión ya persistida, se vuelve a pedir el perfil real
  // antes de dejar renderizar las rutas, mostrando un loader en el medio en vez de
  // dejar que se vea (y desaparezca) una pantalla basada en datos viejos.
  const [sessionSynced, setSessionSynced] = useState(() => !authUser?.token);

  useEffect(() => {
    if (!authUser?.token) return;

    axios.defaults.headers.common.Authorization = `Bearer ${authUser.token}`;
    axios.get(`${config.REACT_APP_BASE_URL}/api/v1/auth/data`)
      .then((res) => setDataUser(res.data))
      .catch(() => { /* si falla, seguimos con lo que ya había en localStorage */ })
      .finally(() => setSessionSynced(true));
    // Solo al bootear la app con la sesión que ya estaba persistida, no en cada
    // cambio de authUser (el login ya deja dataUser fresco por su cuenta).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!authUser?.token) return;

    const checkOneSignal = setInterval(() => {
      if (window.OneSignal?.User?.PushSubscription) {
        setOneSignalReady(true);
        clearInterval(checkOneSignal);
      }
    }, 1000);

    return () => clearInterval(checkOneSignal);
  }, [authUser]);

  // Con multi-dispositivo, el hasPushId del servidor refleja *algún* dispositivo
  // del usuario (p.ej. su teléfono); lo que importa para el prompt y el registro
  // es si ESTE navegador tiene una suscripción push activa.
  useEffect(() => {
    if (!authUser?.token) {
      setHasPushId(null);
      return;
    }
    if (!oneSignalReady) return;

    const granted = typeof Notification !== 'undefined' && Notification.permission === 'granted';
    setHasPushId(Boolean(granted && window.OneSignal?.User?.PushSubscription?.id));
  }, [authUser, oneSignalReady]);

  const registerPushId = async () => {
    try {
      const sub = window.OneSignal?.User?.PushSubscription;
      if (!sub) return false;

      const playerId = sub.id || await sub.getIdAsync();
      if (playerId) {
        await axios.post(
          `${config.REACT_APP_BASE_URL}/api/v1/user/pushId`,
          { push_id: playerId, platform: 'web' },
          { headers: { Authorization: `Bearer ${authUser.token}` } }
        );
        // OneSignal asigna un id de suscripción al navegador apenas carga el SDK,
        // exista o no permiso concedido todavía — no alcanza para considerar que
        // el usuario ya aceptó. Sin este chequeo, el prompt se cerraba solo casi
        // al instante (este método se llama al montar y cada 10s).
        const granted = typeof Notification !== 'undefined' && Notification.permission === 'granted';
        if (granted) setHasPushId(true);
        return true;
      }
    } catch (err) {
      console.error('Error registering push ID:', err);
    }
    return false;
  };

  useEffect(() => {
    if (!authUser?.token) return;

    // Upsert idempotente: garantiza que el token de ESTE navegador quede
    // registrado en el servidor aunque hasPushId ya sea true
    registerPushId();

    if (hasPushId) return;

    const interval = setInterval(() => {
      registerPushId();
    }, 10000);

    return () => clearInterval(interval);
  }, [authUser, hasPushId]);

  useEffect(() => {
    if (!authUser?.token || !window.OneSignal?.User?.PushSubscription) return;

    const onSubscriptionChange = async (event) => {
      if (event?.current?.id) {
        try {
          await axios.post(
            `${config.REACT_APP_BASE_URL}/api/v1/user/pushId`,
            { push_id: event.current.id, platform: 'web' },
            { headers: { Authorization: `Bearer ${authUser.token}` } }
          );
          setHasPushId(true);
        } catch (err) {
          console.error('Error registering push ID on change:', err);
        }
      }
    };

    const sub = window.OneSignal.User.PushSubscription;
    sub.addEventListener('change', onSubscriptionChange);
    return () => sub.removeEventListener('change', onSubscriptionChange);
  }, [authUser]);

  // Deep-link: clic en una push de chat/regalo → navegar a la conversación
  // (mismo contrato de payload que la app React Native: additionalData.chat_id)
  useEffect(() => {
    if (!oneSignalReady || !window.OneSignal?.Notifications?.addEventListener) return;

    const onNotificationClick = (event) => {
      const data = event?.notification?.additionalData;
      // El backend manda un deep_link canónico (sin rol); acá se traduce a la
      // ruta local. Si no viene, se cae a la deducción por chat_id/tipo.
      const deepLinkPath = webPathFromDeepLink(data?.deep_link, userRole);
      if (deepLinkPath) {
        window.location.href = deepLinkPath;
      } else if (data?.chat_id) {
        window.location.href = `/${userRole || 'client'}/chat/${data.chat_id}`;
      } else if (data?.type === 'pack_suggestion' && data?.pack_id) {
        window.location.href = `/${userRole || 'model'}/packs/edit/${data.pack_id}`;
      }
    };

    window.OneSignal.Notifications.addEventListener('click', onNotificationClick);
    return () => window.OneSignal.Notifications.removeEventListener('click', onNotificationClick);
  }, [oneSignalReady, userRole]);

  const notificationPermission = typeof Notification !== 'undefined' ? Notification.permission : 'denied';
  const showNotificationPrompt = authUser?.token
    && oneSignalReady
    && hasPushId === false
    && notificationPermission !== 'granted'
    && !dismissedPrompt;

  const handleAcceptNotifications = async () => {
    try {
      if (window.OneSignal?.Notifications?.requestPermission) {
        await window.OneSignal.Notifications.requestPermission();
      }
    } catch (e) {
      console.error('Error requesting notification permission:', e);
    }
    registerPushId();
    setDismissedPrompt(true);
  };

  if (!sessionSynced) {
    return <Loading />;
  }

  return (
    <>
      {showNotificationPrompt && (
        <NotificationPromptModal
          onAccept={handleAcceptNotifications}
          onLater={() => setDismissedPrompt(true)}
          isDenied={notificationPermission === 'denied'}
          darkMode={darkMode}
        />
      )}
      <VideochatHost />
      <PresenceHost />
      <Navigator />
    </>
  );
}

function App() {
  return (
    <AccessGate>
      <Provider>
        <DarkModeProvider>
          <AppContent />
        </DarkModeProvider>
      </Provider>
    </AccessGate>
  );
}

export default App;